import React from 'react';
import { Box, Grid, Paper, SelectChangeEvent, Typography } from '@mui/material';
import { useResizeDetector } from 'react-resize-detector';
import makeStyles from '@mui/styles/makeStyles';
import SquareIcon from '@mui/icons-material/Square';
import { FormattedMessage } from 'react-intl';
import SourceDropDown from '../SourceByTimeChart/SourceDropDown';
import HeaderSkeleton from '../UtilizationChart/HeaderSkeleton';
import { DropdownLabel, DropdownOptionType } from './utils';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    marginRight: theme.spacing(3),
    flexDirection: 'column',
    boxShadow: 'none',
    paddingBottom: theme.spacing(1)
  },
  header: {
    display: 'flex',
    padding: theme.spacing(1.5),
    width: '100%'
  },
  headerRight: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end'
  },
  hourAnotation: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row'
  },
  minHour: {
    marginRight: theme.spacing(1)
  },
  maxHours: {
    marginLeft: theme.spacing(1)
  },
  gradient: {
    width: theme.spacing(10),
    height: theme.spacing(1),
    backgroundImage:
      'linear-gradient(to right, #fff , rgba(38, 210, 147, 1) 100%)'
  },
  overTime: {
    display: 'flex',
    margin: theme.spacing(0, 4, 0, 3),
    flexDirection: 'row',
    alignSelf: 'center'
  },
  overTimeIcon: {
    width: theme.spacing(1),
    height: theme.spacing(1),
    border: '4px solid transparent',
    borderTopColor: 'rgba(133, 109, 219, 1)',
    borderRightColor: 'rgba(133, 109, 219, 1)',
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.63)
  },
  content: {
    display: 'flex',
    width: '100%',
    marginTop: theme.spacing(1),
    paddign: theme.spacing(1)
  },
  legendText: {
    color: theme.palette.text.secondary
  },
  legendBox: {
    paddingLeft: theme.spacing(2),
    display: 'flex',
    alignItems: 'center'
  },
  legendIcon: {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5),
    marginRight: theme.spacing(0.4)
  }
}));

type MyTeamHoursLegendProps = {
  onSelectValueChange: (event: SelectChangeEvent) => void;
  selectvalue: string;
  dropDownOptions: DropdownOptionType[];
  loading?: boolean;
  categoriesLabels?: Record<string, string>;
  disabled?: boolean | undefined;
};

const MyTeamHoursLegend: React.FC<MyTeamHoursLegendProps> = ({
  onSelectValueChange,
  selectvalue,
  dropDownOptions,
  loading,
  categoriesLabels,
  disabled
}: MyTeamHoursLegendProps) => {
  const classes = useStyles();
  const { ref: chartRef } = useResizeDetector();
  return (
    <Paper
      className={classes.container}
      data-testid={'my-team-hours-legend-container'}
      ref={chartRef}
    >
      <Box
        className={classes.header}
        data-testid={'my-team-hours-legend-header'}
      >
        <div className={classes.headerRight}>
          {selectvalue === DropdownLabel.hours && (
            <div
              className={classes.hourAnotation}
              data-testid={'my-team-hours-legend-hoursAnnotaion'}
            >
              <Typography variant="caption" className={classes.minHour}>
                <FormattedMessage id={'insights.myTeamHoursLegend.minHour'} />
              </Typography>
              <div className={classes.gradient} />
              <Typography variant="caption" className={classes.maxHours}>
                <FormattedMessage id={'insights.myTeamHoursLegend.maxHours'} />
              </Typography>
            </div>
          )}
          {loading && selectvalue === DropdownLabel.utilization ? (
            <HeaderSkeleton />
          ) : (
            selectvalue === DropdownLabel.utilization &&
            categoriesLabels &&
            Object.entries(categoriesLabels).map((x) => (
              <Grid key={x[0]} item className={classes.legendBox}>
                <SquareIcon
                  fontSize="small"
                  sx={{ color: x[1] }}
                  className={classes.legendIcon}
                  data-testid={`legend-square-box-${x[0]}`}
                />
                <Typography
                  variant="caption"
                  className={classes.legendText}
                  data-testid={`legend-name-${x[0]}`}
                >
                  {x[0]}
                </Typography>
              </Grid>
            ))
          )}
          <div className={classes.overTime}>
            <div
              className={classes.overTimeIcon}
              data-testid={'my-team-hours-legend-overtimeIcon'}
            />
            <Typography variant="caption">
              <FormattedMessage id={'insights.myTeamHoursLegend.overTime'} />
            </Typography>
          </div>
          <div>
            <SourceDropDown
              onChange={onSelectValueChange}
              value={selectvalue}
              options={dropDownOptions}
              disabled={Boolean(disabled)}
            />
          </div>
        </div>
      </Box>
    </Paper>
  );
};
export default React.memo(MyTeamHoursLegend);
